import { HttpClient } from '@angular/common/http';
import {
    computed,
    DestroyRef,
    inject,
    Injectable,
    linkedSignal,
} from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { injectUser } from '@quipex/core/auth';
import { APP_CONFIG_TOKEN, createContactUsHref } from '@quipex/core/config';
import { createHttpContextFromOptions } from '@quipex/core/primitives';
import { UsersApiService } from '@quipex/shared/contracts';
import { map } from 'rxjs';
import { parseIndex } from './models';

@Injectable()
export class ReadStatus {
    private readonly http = inject(HttpClient);
    private readonly api = inject(UsersApiService);
    private readonly user = injectUser();
    private readonly destroyRef = inject(DestroyRef);
    private readonly config = inject(APP_CONFIG_TOKEN);
    private readonly enabled =
        createContactUsHref(this.config.supportEmail) !== null;
    private readonly userId = computed(() => this.user()?.id);
    private readonly confirmedVersions = linkedSignal({
        source: this.userId,
        computation: () => new Set<string>(),
    });
    private readonly pendingVersions = new Set<string>();
    private readonly index = rxResource({
        params: () => (this.enabled ? this.userId() : undefined),
        stream: () =>
            this.http
                .get<unknown>('/assets/content/whats-new/index.json')
                .pipe(map(parseIndex)),
    });
    private readonly latest = computed(() =>
        this.index.hasValue() ? this.index.value()[0] : undefined
    );
    private readonly status = rxResource({
        params: () => {
            const userId = this.userId();
            const version = this.latest()?.slug;
            return userId && version ? { userId, version } : undefined;
        },
        stream: ({ params }) =>
            this.api.getCurrentUserReleaseNotesReadStatus(params.version, {
                context: createHttpContextFromOptions({
                    // Background discovery keeps the launcher usable without a startup error toast.
                    suppressAutoErrorToast: true,
                }),
            }),
    });

    public readonly hasUnread = computed(() => {
        const version = this.latest()?.slug;
        return Boolean(
            version &&
            this.status.hasValue() &&
            !this.status.value().isRead &&
            !this.confirmedVersions().has(version)
        );
    });

    public markRead(version: string): void {
        const userId = this.userId();
        const key = `${userId}:${version}`;
        if (
            !userId ||
            this.confirmedVersions().has(version) ||
            this.pendingVersions.has(key)
        )
            return;

        this.pendingVersions.add(key);
        this.api
            .createUserMeReleaseNoteReadStatu(version)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.pendingVersions.delete(key);
                    if (this.userId() === userId) {
                        this.confirmedVersions.update(
                            (versions) => new Set([...versions, version])
                        );
                    }
                },
                error: () => this.pendingVersions.delete(key),
            });
    }
}
